import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import ColdStartLoader from "../components/ColdStartLoader";
import { Eye, EyeOff, Fingerprint } from "lucide-react";
import {
  isBiometricSupported,
  isPlatformAuthenticatorAvailable,
  checkBiometricStatus,
  authenticateWithBiometric,
  getBiometricUsername,
  saveBiometricUsername
} from "../utils/biometric";
import { syncAllFocusWithBackend } from "@/lib/notificationService";
import { handleLoginUserChange } from "../utils/userDataIsolation";
import { userStorage } from "../utils/userStorage";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

export default function Login() {
  // Check localStorage synchronously at init to prevent flickering
  const savedBiometricUser = getBiometricUsername();
  const hasBiometricSetup = !!savedBiometricUser && isBiometricSupported();
  
  const [formData, setFormData] = useState({
    username: "",
    password: ""
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(hasBiometricSetup);
  const [biometricUsername, setBiometricUsername] = useState(savedBiometricUser || "");
  const [showPasswordForm, setShowPasswordForm] = useState(!hasBiometricSetup);
  const [checkingBiometric, setCheckingBiometric] = useState(hasBiometricSetup);
  const navigate = useNavigate();

  // Try auto-login if biometric is set up
  useEffect(() => {
    let isMounted = true;
    
    const tryBiometricLogin = async () => {
      // Only proceed if biometric was detected at init
      if (!hasBiometricSetup) return;
      
      try {
        // Verify with server that biometric is still available
        const status = await checkBiometricStatus(savedBiometricUser);
        
        if (!status.biometric_available) {
          // Server says no biometric - show password form
          if (isMounted) {
            setBiometricAvailable(false);
            setCheckingBiometric(false);
            setShowPasswordForm(true);
          }
          return;
        }
        
        // Try automatic biometric login
        if (isMounted) {
          setLoading(true);
          try {
            const result = await authenticateWithBiometric(savedBiometricUser, false);
            
            if (result && result.access_token && isMounted) {
              await handleSuccessfulLogin(result.access_token);
              return;
            }
          } catch (autoError) {
            console.log("[Biometric] Auto-login cancelled:", autoError.message);
          }
          
          // Auto-login failed/cancelled - show manual button
          if (isMounted) {
            setLoading(false);
            setCheckingBiometric(false);
          }
        }
      } catch (error) {
        console.error("[Biometric] Error:", error);
        if (isMounted) {
          setCheckingBiometric(false);
          setShowPasswordForm(true);
        }
      }
    };
    
    tryBiometricLogin();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const handleSuccessfulLogin = async (token) => {
    console.log("handleSuccessfulLogin called with token:", token ? "present" : "missing");
    localStorage.setItem('token', token);

    try {
      const userResponse = await axios.get(`${API}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log("User data received:", userResponse.data?.username);

      // User-Data-Isolation: Bei User-Wechsel auf gleichem Gerät lokale Daten des alten Users räumen
      // Muss VOR dem dayFocus-Sync passieren, damit Daten von User A nicht ins Backend von B geschrieben werden
      const currentUserId = userResponse.data?.id || userResponse.data?.username;
      if (currentUserId) {
        handleLoginUserChange(currentUserId);
      }

      // Sync today + tomorrow focus from localStorage to backend (jetzt mit garantiert richtigen Daten)
      try {
        const dayFocusData = userStorage.getItem('zielplanung_day_focus');
        if (dayFocusData) {
          const dayFocus = JSON.parse(dayFocusData);
          await syncAllFocusWithBackend(dayFocus);
        }
      } catch (e) {
        console.log('Focus sync skipped:', e.message);
      }

      // Save username for biometric
      saveBiometricUsername(userResponse.data.username);
      
      if (userResponse.data.has_donated) {
        toast.success("Willkommen zurück!");
        navigate('/main-hub');
      } else {
        // Spendenseite nur 1× pro Woche anzeigen, nicht bei jedem Login
        const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
        const lastShown = localStorage.getItem('donation_last_shown');
        const shouldShow = !lastShown || (Date.now() - parseInt(lastShown, 10)) > WEEK_MS;
        if (shouldShow) {
          localStorage.setItem('donation_last_shown', Date.now().toString());
          toast.success("Erfolgreich eingeloggt!");
          navigate('/support');
        } else {
          toast.success("Willkommen zurück!");
          navigate('/main-hub');
        }
      }
    } catch (error) {
      console.error("Error getting user data:", error);
      toast.success("Erfolgreich eingeloggt!");
      navigate('/main-hub');
    }
  };

  const handleBiometricLogin = async (username) => {
    if (loading) return;
    if (!username) {
      setShowPasswordForm(true);
      return;
    }
    
    setLoading(true);
    try {
      const result = await authenticateWithBiometric(username);
      
      if (result && result.access_token) {
        await handleSuccessfulLogin(result.access_token);
      } else {
        throw new Error("Kein Token erhalten");
      }
    } catch (error) {
      console.error("Biometric login failed:", error.message);
      toast.error("Fingerabdruck-Login fehlgeschlagen. Bitte mit Passwort anmelden.");
      setShowPasswordForm(true);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formPayload = new FormData();
      formPayload.append('username', formData.username);
      formPayload.append('password', formData.password);

      const response = await axios.post(`${API}/api/auth/login`, formPayload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      await handleSuccessfulLogin(response.data.access_token);
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.detail === "email_not_verified") {
        toast.error("Bitte bestätige zuerst deine Email-Adresse. Prüfe dein Postfach!");
      } else {
        toast.error(error.response?.data?.detail || "Login fehlgeschlagen");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <ColdStartLoader message="Anmeldung läuft..." />}
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center px-6">
        <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="/burningteam-logo.png" 
              alt="BurningTeam Logo" 
              className="h-20 w-auto"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-800">
            Anmelden
          </CardTitle>
          <p className="text-slate-500 mt-2">
            Melden Sie sich in Ihrem Organisationshilfe-Konto an
          </p>
          <div className="mt-3">
            <span className="inline-block px-3 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">
              Beta Version
            </span>
          </div>
        </CardHeader>
        <CardContent>
          {/* Loading state while checking/attempting biometric */}
          {checkingBiometric && (
            <div className="text-center py-8">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Fingerprint className="w-10 h-10 text-white animate-pulse" />
              </div>
              <p className="text-slate-700 font-semibold text-lg">
                {biometricUsername ? `Hallo ${biometricUsername}!` : "Wird geprüft..."}
              </p>
              <p className="text-slate-500 mt-1">
                {loading ? "Bitte Fingerabdruck/Face ID bestätigen..." : "Biometrie wird vorbereitet..."}
              </p>
              <button
                type="button"
                onClick={() => {
                  setCheckingBiometric(false);
                  setShowPasswordForm(true);
                  setLoading(false);
                }}
                className="mt-6 text-sm text-slate-500 hover:text-slate-700 underline"
              >
                Mit Passwort anmelden
              </button>
            </div>
          )}

          {/* Biometric Login Button - shown after auto-login failed/cancelled */}
          {!checkingBiometric && !showPasswordForm && biometricAvailable && (
            <div className="text-center py-6">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Fingerprint className="w-10 h-10 text-white" />
              </div>
              <p className="text-slate-700 font-semibold text-lg mb-1">
                Hallo {biometricUsername}!
              </p>
              <p className="text-slate-500 text-sm mb-4">
                Tippe zum Anmelden
              </p>
              <button
                type="button"
                onClick={() => handleBiometricLogin(biometricUsername)}
                disabled={loading}
                className="w-full py-4 bg-gradient-to-br from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-xl flex items-center justify-center gap-3 transition-all duration-200 shadow-lg hover:shadow-xl active:scale-[0.98] disabled:opacity-50"
              >
                <Fingerprint className={`w-7 h-7 ${loading ? 'animate-pulse' : ''}`} />
                <span className="text-lg font-semibold">
                  {loading ? "Anmeldung läuft..." : "Mit Fingerabdruck anmelden"}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setShowPasswordForm(true)}
                className="mt-4 text-sm text-slate-500 hover:text-slate-700 underline"
              >
                Stattdessen mit Passwort anmelden
              </button>
            </div>
          )}

          {/* Password Form */}
          {showPasswordForm && (
            <>
              <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
                <div>
                  <Label htmlFor="username">Benutzername</Label>
                  <Input
                    id="username"
                    name="username"
                    type="text"
                    autoComplete="username"
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                    required
                    placeholder="Ihr Benutzername"
                  />
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label htmlFor="password">Passwort</Label>
                    <Link 
                      to="/forgot-password"
                      className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                    >
                      Passwort vergessen?
                    </Link>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      required
                      placeholder="Ihr Passwort"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
                  disabled={loading}
                >
                  Anmelden
                </Button>
              </form>

              {/* Back to biometric button */}
              {biometricAvailable && (
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordForm(false);
                      setCheckingBiometric(true);
                      handleBiometricLogin(biometricUsername);
                    }}
                    className="inline-flex items-center text-sm text-emerald-600 hover:text-emerald-700"
                  >
                    <Fingerprint className="w-4 h-4 mr-2" />
                    Mit Fingerabdruck anmelden
                  </button>
                </div>
              )}
            </>
          )}

          <div className="mt-6 text-center">
            <p className="text-slate-600">
              Noch kein Konto? {" "}
              <Link 
                to="/register" 
                className="text-emerald-600 hover:text-emerald-700 font-medium"
              >
                Hier registrieren
              </Link>
            </p>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-200 text-center">
            <p className="text-sm text-slate-500 mb-3">
              Gefällt Ihnen diese App? Unterstützen Sie die Entwicklung!
            </p>
            <a 
              href="https://www.paypal.com/donate/?hosted_button_id=WPK5YE395ZHUJ"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-[#0070ba] hover:bg-[#005ea6] text-white rounded-lg transition-colors duration-200 text-sm font-medium"
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a7.7 7.7 0 0 1-.32 1.433c-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9L7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81.298.34.563.729.774 1.107z"/>
              </svg>
              Spenden via PayPal
            </a>
          </div>
        </CardContent>
      </Card>
      </div>
    </>
  );
}